import { AnalyticsEventName, FeatureFlags } from '@dupe/common/constants'
import dynamic from 'next/dynamic'
import { useEffect } from 'react'
import ModalDialog from '~/src/components/ModalDialog'
import { showModalClose } from '~/src/components/auth/showModalClose'
import LoaderIcon from '~/src/components/svg/LoaderIcon'
import { useAuthContext } from '~/src/context/AuthProvider'
import { useSearchContext } from '~/src/context/SearchProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import useAuthUser from '~/src/hooks/useAuthUser'
import useDeviceDetect from '~/src/hooks/useDeviceDetect'
import { useAuthWebToken } from '~/src/hooks/useAuthWebToken'
import useInviteCodeFromUrl from '~/src/hooks/useInviteCodeFromUrl'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import {
  BRAND_ONBOARDING_MODAL_WIDTH,
  RECAPTCA_ID,
  RESULTS_SIGNUP_WALL_SOURCE,
} from '~/src/lib/constants'

// AuthContent drags in the whole phone-auth stack (firebase/auth flows,
// react-phone-number-input + libphonenumber metadata, the onboarding steps).
// The modal renders its children only while open, so loading the chunk on
// first open keeps all of it out of every page's initial bundle.
const AuthContent = dynamic(() => import('./AuthContent'), {
  ssr: false,
  loading: () => (
    <div className='flex min-h-[200px] items-center justify-center'>
      <div className='animate-spin'>
        <LoaderIcon height={24} />
      </div>
    </div>
  ),
})

const AuthModal: React.FC = () => {
  const analytics = useAnalytics()

  // Detect and store invite code from URL
  const { inviteCode: inviteCodeFromUrl, clearInviteCode } =
    useInviteCodeFromUrl()

  const { authUser } = useAuthUser()
  const { isAuthWebTokenActive, wasAuthWebTokenActive } = useAuthWebToken()
  const {
    isOpen,
    closeAuthModal,
    authSource,
    disableClose,
    fullScreen,
    blurOverlay,
    handleAuthSuccess,
    openAuthModal,
    onboardingActive,
    onboardingCloseable,
  } = useAuthContext()

  const { resetProcessing } = useSearchContext()
  const { isMobile } = useDeviceDetect()

  // Onboarding is now unified: BOTH the results wall and the standalone signup
  // render it through `PostSignupOnboarding`. AuthContent publishes
  // `onboardingActive` the moment that flow is actually on screen (and not the
  // already-onboarded skip), which is the robust "onboarding is showing" signal —
  // authSource alone is insufficient because authenticated users open the modal
  // for non-onboarding reasons (profile, preferences, rewards…) and must NOT get
  // the onboarding width.
  //
  // Once onboarding shows we drop out of full-screen and constrain the shell to a
  // centered modal — the panel's transition-all animates the change from the
  // full-screen sign-up step. Onboarding is single-column and hugs its content at
  // BRAND_ONBOARDING_MODAL_WIDTH; the 900px variant went with the two-column
  // question flow it was for. Every non-onboarding state keeps the standard 600px.
  const modalWidth = onboardingActive ? BRAND_ONBOARDING_MODAL_WIDTH : 600

  // Never full-screen once onboarding is showing, even if the sign-up step that
  // preceded it was full-screen (the standalone signup opens full-screen).
  const effectiveFullScreen = fullScreen && !onboardingActive

  // Bottom-sheet on phones for the whole results-wall flow (its locked gate + its
  // onboarding) AND for the standalone signup onboarding — matching the wall's
  // onboarding treatment for consistency. The standalone signup's pre-auth gate
  // stays full-screen (onboardingActive false, not the wall) and so is unaffected.
  const wallFlow = authSource === RESULTS_SIGNUP_WALL_SOURCE
  const bottomSheet = (wallFlow || onboardingActive) && isMobile

  const { featureOn: rewardsProgramFeatureEnabled } = useIsFeatureOn(
    FeatureFlags.RewardsProgram
  )

  // If invite code is in url, open auth modal
  useEffect(() => {
    if (!rewardsProgramFeatureEnabled) return
    if (isOpen) return
    if (authUser) return

    if (inviteCodeFromUrl) {
      openAuthModal({
        source: 'invite-code',
        modalTitle: `Join your friend on Dupe.com`,
        modalSubtitle: `Invite code ${inviteCodeFromUrl}`,
        fullScreen: false,
        blurOverlay: true,
        onClose: () => {
          clearInviteCode()
        },
      })
    }
  }, [
    authUser,
    inviteCodeFromUrl,
    isOpen,
    openAuthModal,
    clearInviteCode,
    rewardsProgramFeatureEnabled,
  ])

  return (
    <>
      <div id={RECAPTCA_ID} className='flex items-center justify-center' />
      <ModalDialog
        show={isOpen}
        fullScreen={effectiveFullScreen}
        bottomSheet={bottomSheet}
        blurOverlay={blurOverlay}
        width={effectiveFullScreen ? undefined : modalWidth}
        onClose={() => {
          closeAuthModal()
        }}
        showCloseButton={showModalClose({
          disableClose: !!disableClose,
          onboardingActive,
          onboardingCloseable,
        })}
        onCloseButtonClick={() => {
          // Handle modal close based on auth state:
          // 1. If user is not authenticated, reset processing state
          // 2. If user is authenticated but hasn't completed onboarding,
          //    execute callback to maintain authenticated state
          if (!authUser) {
            resetProcessing()
          } else if (
            handleAuthSuccess &&
            !isAuthWebTokenActive &&
            !wasAuthWebTokenActive
          ) {
            handleAuthSuccess()
          }
          analytics.track(AnalyticsEventName.AuthModalClosed, {
            source: authSource,
            // DUP-1903: when the X is the exit during onboarding (the
            // `onboarding_first` arm, where the prominent skip is removed), tag
            // the dismissal so it's distinguishable from a plain modal close.
            onboardingDismiss: onboardingActive && onboardingCloseable,
            ...(onboardingActive && onboardingCloseable
              ? { optinPlacement: 'onboarding_first' }
              : {}),
          })
        }}
        disableClickOutsideClose
        closeOnSwipeDown={false}
        customClassNames='flex flex-col justify-center'
      >
        {({ closeModal }) => <AuthContent closeCallback={closeModal} />}
      </ModalDialog>
    </>
  )
}

export default AuthModal
